//@ts-nocheck
import FieldSection from "components/shared/Tab/FieldSection";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Fade,
  Tooltip,
} from "@mui/material";
import DynamicInput from "./DynamicInput";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  GROUP_TYPE,
  TRIP_TYPE,
  TRIPAGEOPTION,
  TRIPDAYSOPTION,
  TRIP_SCOPE,
  COUNTRY_OPTIONS,
  INTERNATIONAL,
  DEFAULT_DOMESTIC_COUNTRY,
  AIRPORT_COUNTRY_OPTIONS,
  getStatesForCountry,
  getPrimaryAirport,
} from "screens/create-new-trip/utils/utils";
import { useEffect, useState } from "react";
import CountryStateDistrict from "utils/Data";

interface TripDetailsProps {
  formik: any;
  editable: boolean;
}

const TripDetails = ({ formik, editable = false }: TripDetailsProps) => {
  const [locationOptions, setLocationOptions] = useState({
    startLoc: {},
    endLoc: {},
    destination: { states: [], district: [] },
  });

  // Load states on initial render
  useEffect(() => {
    const states = CountryStateDistrict.getStatesByCountryId(1) as any;

    // Initialize the state options for destination
    setLocationOptions((prev) => ({
      ...prev,
      destination: { states, district: [] },
    }));

    if (
      !formik.values.location?.destination ||
      formik.values.location.destination.length === 0
    ) {
      formik.setFieldValue("location.destination", [
        {
          country: "India",
          state: "",
          district: "",
        },
      ]);
    }
  }, []);

  // Initialize location options when component mounts with existing data
  useEffect(() => {
    if (!formik.values.location) return;

    const initializeAllLocationOptions = () => {
      const newLocationOptions = { ...locationOptions };

      // Initialize startLoc options
      if (formik.values.location.startLoc) {
        const startLocOptions = {};
        formik.values.location.startLoc.forEach(
          (location: any, index: number) => {
            const states = CountryStateDistrict.getStatesByCountryId(1) as any;

            let districts = [];
            if (location?.state) {
              const stateObj = CountryStateDistrict.getAllStates().find(
                (s) => s.name === location.state
              );
              if (stateObj) {
                districts = CountryStateDistrict.getCitiesByStateId(
                  stateObj.id
                ) as never[];
              }
            }

            startLocOptions[index] = { states, district: districts };
          }
        );
        newLocationOptions.startLoc = startLocOptions;
      }

      // Initialize endLoc options
      if (formik.values.location.endLoc) {
        const endLocOptions = {};
        formik.values.location.endLoc.forEach((location, index) => {
          const states = CountryStateDistrict.getStatesByCountryId(1) as any;

          let districts = [];
          if (location?.state) {
            const stateObj = CountryStateDistrict.getAllStates().find(
              (s) => s.name === location.state
            );
            if (stateObj) {
              districts = CountryStateDistrict.getCitiesByStateId(
                stateObj.id
              ) as never[];
            }
          }

          endLocOptions[index] = { states, district: districts };
        });
        newLocationOptions.endLoc = endLocOptions;
      }

      // Initialize destination options if destination exists
      if (
        formik.values.location.destination &&
        formik.values.location.destination.length > 0
      ) {
        const destinationLocation = formik.values.location.destination[0];
        const states = CountryStateDistrict.getStatesByCountryId(
          1
        ) as StateDistrictData[];

        let districts: StateDistrictData[] = [];
        if (destinationLocation?.state) {
          const stateObj = CountryStateDistrict.getAllStates().find(
            (s: StateDistrictData) => s.name === destinationLocation.state
          );
          if (stateObj) {
            districts = CountryStateDistrict.getCitiesByStateId(
              stateObj.id
            ) as StateDistrictData[];
          }
        }

        newLocationOptions.destination = { states, district: districts };
      }

      setLocationOptions(newLocationOptions);
    };

    // Only initialize if we have location data and haven't initialized yet
    if (
      formik.values.location &&
      (formik.values.location.startLoc?.length > 0 ||
        formik.values.location.endLoc?.length > 0 || 
        formik.values.location.destination?.length > 0)
    ) {
      initializeAllLocationOptions();
    }
  }, [formik.values.location?.startLoc, formik.values.location?.endLoc,formik.values.location?.destination]); 

  // Handle district loading when state changes
  useEffect(() => {
    if (!formik.values.location) return;

    // Handle destination districts
    const getDestinationDistricts = () => {
      const stateValue = formik.values.location.destination?.[0]?.state;
      if (!stateValue) return [];

      const stateObj = CountryStateDistrict.getAllStates().find(
        (s) => s.name === stateValue
      );

      if (!stateObj) return [];
      return CountryStateDistrict.getCitiesByStateId(stateObj.id) as never[];
    };

    // Only update destination districts here
    setLocationOptions((prev) => ({
      ...prev,
      destination: {
        ...prev.destination,
        district: getDestinationDistricts(),
      },
    }));
  }, [formik.values.location?.destination?.[0]?.state]);

  const isInternational = formik.values.tripScope === INTERNATIONAL;

  // Switching scope resets every location. Domestic => India + state + district.
  // International => country selectable (start/end also pick state + auto airport;
  // destination is country-only).
  const handleScopeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const scope = e.target.value;
    formik.setFieldValue("tripScope", scope);

    const goingIntl = scope === INTERNATIONAL;
    const country = goingIntl ? "" : DEFAULT_DOMESTIC_COUNTRY;
    const resetLocations = (arr: any[] = []) =>
      (arr.length ? arr : [{}]).map(() => ({
        country,
        state: "",
        district: "",
        airport: "",
      }));

    formik.setFieldValue("location.startLoc", resetLocations(formik.values.location?.startLoc));
    formik.setFieldValue("location.endLoc", resetLocations(formik.values.location?.endLoc));
    formik.setFieldValue("location.destination", [
      { country, state: "", district: "", airport: "" },
    ]);
  };

  const handleLocationChange = (
    locationType: "startLoc" | "endLoc" | "destination",
    index: number,
    field: string,
    value: string
  ) => {
    formik.setFieldValue(field, value);

    if (field.includes("country")) {
      const states = CountryStateDistrict.getStatesByCountryId(1);

      if (locationType === "destination") {
        setLocationOptions((prev) => ({
          ...prev,
          destination: { states, district: [] },
        }));
        formik.setFieldValue(`location.${locationType}[0].state`, "");
        formik.setFieldValue(`location.${locationType}[0].district`, "");
      } else {
        setLocationOptions((prev) => ({
          ...prev,
          [locationType]: {
            ...prev[locationType],
            [index]: { states, district: [] },
          },
        }));
        formik.setFieldValue(`location.${locationType}[${index}].state`, "");
        formik.setFieldValue(`location.${locationType}[${index}].district`, "");
      }
    }

    if (field.includes("state")) {
      const stateId = CountryStateDistrict.getAllStates().find(
        (s) => s.name === value
      )?.id;
      const districts = CountryStateDistrict.getCitiesByStateId(stateId || 0);

      if (locationType === "destination") {
        setLocationOptions((prev) => ({
          ...prev,
          destination: { ...prev.destination, district: districts },
        }));
        formik.setFieldValue(`location.${locationType}[0].district`, "");
      } else {
        setLocationOptions((prev) => ({
          ...prev,
          [locationType]: {
            ...prev[locationType],
            [index]: { ...prev[locationType][index], district: districts },
          },
        }));
        formik.setFieldValue(`location.${locationType}[${index}].district`, "");
      }
    }
  };

  // Add new location
  const addLocation = (locationType: "startLoc" | "endLoc") => {
    const currentLocations = formik.values.location?.[locationType] || [];
    const newLocation = {
      country: "India",
      state: "",
      district: "",
    };

    const updatedLocations = [...currentLocations, newLocation];
    formik.setFieldValue(`location.${locationType}`, updatedLocations);

    // Initialize options for the new location
    const newIndex = updatedLocations.length - 1;
    const states = CountryStateDistrict.getStatesByCountryId(1) as any;

    setLocationOptions((prev) => ({
      ...prev,
      [locationType]: {
        ...prev[locationType],
        [newIndex]: { states, district: [] },
      },
    }));
  };

  // Remove location
  const removeLocation = (
    locationType: "startLoc" | "endLoc",
    index: number
  ) => {
    const currentLocations = formik.values.location?.[locationType] || [];
    const updatedLocations = currentLocations.filter((_, i) => i !== index);
    formik.setFieldValue(`location.${locationType}`, updatedLocations);

    // Rebuild location options after removal
    const newOptions = {};
    updatedLocations.forEach((location, i) => {
      const states = CountryStateDistrict.getStatesByCountryId(1) as any;

      // Initialize districts if state is selected
      let districts = [];
      if (location?.state) {
        const stateObj = CountryStateDistrict.getAllStates().find(
          (s) => s.name === location.state
        );
        if (stateObj) {
          districts = CountryStateDistrict.getCitiesByStateId(
            stateObj.id
          ) as never[];
        }
      }

      newOptions[i] = { states, district: districts };
    });

    setLocationOptions((prev) => ({
      ...prev,
      [locationType]: newOptions,
    }));
  };

  const renderSingleLocationFields = (
    locationType: "destination",
    label: string
  ) => (
    <FieldSection label={label}>
      <Box display="flex" gap={2}>
        {isInternational ? (
          /* International: country-only selection (no state/district). */
          <DynamicInput
            name={`location.${locationType}[0].country`}
            type="select"
            placeholder="Select country"
            required
            formik={formik}
            options={COUNTRY_OPTIONS}
            onChange={(e) =>
              formik.setFieldValue(
                `location.${locationType}[0].country`,
                e.target.value
              )
            }
            disabled={!editable}
          />
        ) : (
          <>
            <DynamicInput
              name={`location.${locationType}[0].country`}
              type="text"
              placeholder="Select country"
              required
              formik={formik}
              value="India"
              disabled
            />
            <DynamicInput
              name={`location.${locationType}[0].state`}
              type="select"
              placeholder="Select State"
              required
              formik={formik}
              options={locationOptions[locationType].states.map((s: any) => ({
                value: s.name,
                label: s.name,
              }))}
              onChange={(e) =>
                handleLocationChange(
                  locationType,
                  0,
                  `location.${locationType}[0].state`,
                  e.target.value
                )
              }
              disabled={!editable}
            />
            <DynamicInput
              name={`location.${locationType}[0].district`}
              type="select"
              placeholder="Select District/Town"
              required
              formik={formik}
              options={locationOptions[locationType].district.map((d: any) => ({
                value: d.name,
                label: d.name,
              }))}
              onChange={(e) =>
                handleLocationChange(
                  locationType,
                  0,
                  `location.${locationType}[0].district`,
                  e.target.value
                )
              }
              disabled={
                !editable || !formik.values.location?.[locationType]?.[0]?.state
              }
            />
          </>
        )}
      </Box>
    </FieldSection>
  );

  const renderMultipleLocationFields = (
    locationType: "startLoc" | "endLoc",
    label: string
  ) => {
    const locations = formik.values.location?.[locationType] || [];

    return (
      <FieldSection label={label}>
        <Box>
          {locations.length === 0 ? (
            <Box
              sx={{
                padding: 3,
                textAlign: "center",
                backgroundColor: "#FAFBFC",
                borderRadius: 2,
                border: "1px solid #E8EAED",
                marginBottom: 2,
              }}
            >
              <LocationOnIcon
                sx={{ fontSize: 40, color: "#D1D5DB", marginBottom: 1 }}
              />
              <Typography color="#6B7280" sx={{ fontSize: "14px" }}>
                No {label.toLowerCase()} added yet. Click the button below to
                add your first location.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginBottom: 2,
              }}
            >
              {locations.map((location, index) => (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #E0E3E7",
                    borderRadius: "12px",
                    padding: 2,
                    backgroundColor: "#FAFBFC",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#C1C7CD",
                      backgroundColor: "#F8F9FA",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="#181D27"
                      fontWeight={600}
                      sx={{ fontSize: "14px" }}
                    >
                      Location {index + 1}
                    </Typography>

                    {editable && locations.length > 1 && (
                      <Tooltip title="Remove this location" arrow>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          onClick={() => removeLocation(locationType, index)}
                          sx={{
                            color: "#6B7280",
                            "&:hover": {
                              color: "#DC2626",
                              backgroundColor: "rgba(220, 38, 38, 0.1)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box display="flex" gap={2}>
                    {isInternational ? (
                      /* International start/end: country -> state -> auto-selected airport. */
                      <>
                        <DynamicInput
                          name={`location.${locationType}[${index}].country`}
                          type="select"
                          placeholder="Select country"
                          required
                          formik={formik}
                          options={AIRPORT_COUNTRY_OPTIONS}
                          onChange={(e) => {
                            const country = e.target.value;
                            formik.setFieldValue(`location.${locationType}[${index}].country`, country);
                            formik.setFieldValue(`location.${locationType}[${index}].state`, "");
                            formik.setFieldValue(`location.${locationType}[${index}].airport`, "");
                          }}
                          disabled={!editable}
                        />
                        <DynamicInput
                          name={`location.${locationType}[${index}].state`}
                          type="select"
                          placeholder="Select State"
                          required
                          formik={formik}
                          options={getStatesForCountry(location?.country)}
                          onChange={(e) => {
                            const state = e.target.value;
                            formik.setFieldValue(`location.${locationType}[${index}].state`, state);
                            formik.setFieldValue(
                              `location.${locationType}[${index}].airport`,
                              getPrimaryAirport(location?.country, state)
                            );
                          }}
                          disabled={!editable || !location?.country}
                        />
                        <DynamicInput
                          name={`location.${locationType}[${index}].airport`}
                          type="text"
                          placeholder="International airport (auto-selected)"
                          required
                          formik={formik}
                          value={location?.airport || ""}
                          disabled
                        />
                      </>
                    ) : (
                      <>
                        <DynamicInput
                          name={`location.${locationType}[${index}].country`}
                          type="text"
                          placeholder="Select country"
                          required
                          formik={formik}
                          value="India"
                          disabled
                        />
                        <DynamicInput
                          name={`location.${locationType}[${index}].state`}
                          type="select"
                          placeholder="Select State"
                          required
                          formik={formik}
                          options={
                            locationOptions[locationType]?.[index]?.states?.map(
                              (s: any) => ({
                                value: s.name,
                                label: s.name,
                              })
                            ) || []
                          }
                          onChange={(e) =>
                            handleLocationChange(
                              locationType,
                              index,
                              `location.${locationType}[${index}].state`,
                              e.target.value
                            )
                          }
                          disabled={!editable}
                        />
                        <DynamicInput
                          name={`location.${locationType}[${index}].district`}
                          type="select"
                          placeholder="Select District/Town"
                          required
                          formik={formik}
                          options={
                            locationOptions[locationType]?.[index]?.district?.map(
                              (d: any) => ({
                                value: d.name,
                                label: d.name,
                              })
                            ) || []
                          }
                          onChange={(e) =>
                            handleLocationChange(
                              locationType,
                              index,
                              `location.${locationType}[${index}].district`,
                              e.target.value
                            )
                          }
                          disabled={
                            !editable ||
                            !formik.values.location?.[locationType]?.[index]?.state
                          }
                        />
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Add New Location Button */}
          {editable && (
            <Fade in={true} timeout={300}>
              <Box
                onClick={() => addLocation(locationType)}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  color: "#374151",
                  fontWeight: 500,
                  border: "2px dashed #D1D5DB",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  minHeight: "48px",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: "#FAFBFC",
                  "&:hover": {
                    backgroundColor: "#F3F4F6",
                    borderColor: "#9CA3AF",
                    color: "#1F2937",
                  },
                  "&:active": {
                    transform: "translateY(1px)",
                  },
                }}
              >
                <AddIcon sx={{ fontSize: "18px" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  Add {label.slice(0, -1)} Location
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>
      </FieldSection>
    );
  };

  return (
    <Box>
      <FieldSection label="Trip Scope">
        <DynamicInput
          name="tripScope"
          type="select"
          placeholder="Select trip scope"
          required
          formik={formik}
          options={TRIP_SCOPE}
          onChange={handleScopeChange}
          disabled={!editable}
        />
      </FieldSection>
      <FieldSection label="Weekend Trip">
        <FormControlLabel
          control={
            <Checkbox
              checked={formik.values.weekend}
              onChange={(e) =>
                formik.setFieldValue("weekend", e.target.checked)
              }
              disabled={!editable}
            />
          }
          label="Tick if the trip is scheduled on a weekend"
        />
      </FieldSection>
      <FieldSection label="Trip Title">
        <DynamicInput
          name="title"
          type="text"
          required
          formik={formik}
          placeholder="Enter trip title"
          disabled={!editable}
          limit={25}
        />
      </FieldSection>
      <FieldSection label="Trip Intro">
        <DynamicInput
          name="intro"
          type="textarea"
          required
          formik={formik}
          placeholder="Enter trip intro"
          disabled={!editable}
          limit={500}
        />
      </FieldSection>

      <FieldSection label="Minimum Age">
        <DynamicInput
          name="minimumAge"
          type="select"
          placeholder="Select minimum age"
          required
          formik={formik}
          options={TRIPAGEOPTION}
          disabled={!editable}
        />
      </FieldSection>
      <FieldSection label="Maximum Age">
        <DynamicInput
          name="maximumAge"
          type="select"
          placeholder="Select maximum age"
          required
          formik={formik}
          options={TRIPAGEOPTION}
          disabled={!editable}
        />
      </FieldSection>
      <FieldSection label="Group Type">
        <DynamicInput
          name="group"
          type="select"
          placeholder="Select group type"
          required
          formik={formik}
          options={GROUP_TYPE}
          disabled={!editable}
        />
      </FieldSection>
      <FieldSection label="Trip Type">
        <DynamicInput
          name="tripType"
          type="select"
          placeholder="Select trip type"
          required
          formik={formik}
          options={TRIP_TYPE}
          disabled={!editable}
        />
      </FieldSection>
      <FieldSection label="Trip Days">
        <DynamicInput
          name="days"
          type="select"
          required
          formik={formik}
          placeholder="Select Number of Days"
          disabled={!editable}
          options={TRIPDAYSOPTION}
        />
      </FieldSection>
      <FieldSection label="Trip Nights">
        <DynamicInput
          name="nights"
          type="select"
          required
          formik={formik}
          placeholder="Select Number of Nights"
          disabled={!editable}
          options={TRIPDAYSOPTION}
        />
      </FieldSection>
      {renderMultipleLocationFields("startLoc", "Start Locations")}
      {renderMultipleLocationFields("endLoc", "End Locations")}
      {renderSingleLocationFields("destination", "Destination")}
    </Box>
  );
};

export default TripDetails;
